import { useRef, useState } from 'react';
import type { TrainingHotspotRegion } from '@hcc/types';

const MIN_SIZE = 0.03;

/**
 * Click-drag rectangle editor over the hotspot image. Regions are plain
 * absolutely-positioned divs in normalised 0..1 coordinates — no canvas
 * element needed. Tapping a region deletes it (confirmed by its title).
 */
export function HotspotRegionCanvas({
  imageUrl,
  regions,
  onChange,
}: {
  imageUrl: string;
  regions: TrainingHotspotRegion[];
  onChange: (next: TrainingHotspotRegion[]) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [draftRect, setDraftRect] = useState<TrainingHotspotRegion | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  function toNorm(e: React.PointerEvent): { x: number; y: number } {
    const rect = ref.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  }

  function rectFrom(a: { x: number; y: number }, b: { x: number; y: number }): TrainingHotspotRegion {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    return {
      x: Math.round(x * 1000) / 1000,
      y: Math.round(y * 1000) / 1000,
      w: Math.round(Math.abs(a.x - b.x) * 1000) / 1000,
      h: Math.round(Math.abs(a.y - b.y) * 1000) / 1000,
    };
  }

  return (
    <div>
      <div
        ref={ref}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          startRef.current = toNorm(e);
          setDraftRect(null);
        }}
        onPointerMove={(e) => {
          if (!startRef.current) return;
          setDraftRect(rectFrom(startRef.current, toNorm(e)));
        }}
        onPointerUp={(e) => {
          if (!startRef.current) return;
          const rect = rectFrom(startRef.current, toNorm(e));
          startRef.current = null;
          setDraftRect(null);
          if (rect.w >= MIN_SIZE && rect.h >= MIN_SIZE) {
            onChange([...regions, rect]);
          }
        }}
        className="relative inline-block max-w-full cursor-crosshair touch-none select-none rounded-token-sm border border-line/10"
      >
        <img src={imageUrl} alt="" className="block max-h-96 max-w-full rounded-token-sm" draggable={false} />
        {regions.map((r, i) => (
          <button
            key={i}
            type="button"
            title="Tap to delete this correct area"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onChange(regions.filter((_, j) => j !== i))}
            style={{
              left: `${r.x * 100}%`,
              top: `${r.y * 100}%`,
              width: `${r.w * 100}%`,
              height: `${r.h * 100}%`,
            }}
            className="absolute border-2 border-success bg-success/20 hover:bg-danger/30 hover:border-danger"
          />
        ))}
        {draftRect ? (
          <span
            aria-hidden
            style={{
              left: `${draftRect.x * 100}%`,
              top: `${draftRect.y * 100}%`,
              width: `${draftRect.w * 100}%`,
              height: `${draftRect.h * 100}%`,
            }}
            className="pointer-events-none absolute border-2 border-dashed border-accent bg-accent/10"
          />
        ) : null}
      </div>
      <p className="mt-1.5 text-xs text-fg-muted">
        Drag a rectangle over each correct area. Tap a green area to remove it.
      </p>
    </div>
  );
}
